import { type ButtonSize } from "antd/lib/button";
import { throttle } from "lodash-es";
import { Component, createRef, type ReactNode } from "react";

import { cn } from "@btnet/utilities-core";

import { BTButtonDeprecated } from "commonComponents/btWrappers/BTButtonDeprecated/BTButtonDeprecated";

import "./ReadMore.less";

interface IReadMoreProps {
    entityId?: number;
    children: React.ReactNode;
    numberOfLines: number;
    lineHeight: number;
    showingMore: boolean;
    onShowingMoreToggle: (showingMore: boolean) => void;
    moreText: ReactNode;
    lessText: ReactNode;
    "data-testid"?: string;
    size: ButtonSize;
    onScrollBackToButton?: () => void;
}

interface IReadMoreState {
    isOverflown: boolean;
    shouldShowLessButton: boolean;
}

type ReadMoreStyle = React.CSSProperties & {
    "--rm-lines"?: number;
    "--rm-line-height"?: number;
};

export default class ReadMorePresentational extends Component<IReadMoreProps, IReadMoreState> {
    static defaultProps = {
        lineHeight: 1.5,
        moreText: "Read more",
        lessText: "Read less",
        size: "default",
    };

    state: Readonly<IReadMoreState> = {
        isOverflown: false,
        shouldShowLessButton: false,
    };

    private textRef = createRef<HTMLDivElement>();

    /**
     * ResizeObserver fires once on `observe()` synchronously after mount, outside React's
     * event-handler batching. When many ReadMore instances mount at once (e.g. hundreds of
     * grid cells), the resulting unbatched setState calls cascade into hundreds of blocking
     * render+layout cycles before the browser can paint. We perform the initial overflow
     * measurement inline in componentDidMount (where React batches setState before paint)
     * and skip the first ResizeObserver callback to avoid duplicating that work.
     */
    private skipNextResizeCallback = false;

    private computeOverflowState = (
        textEl: Element,
        { showingMore, numberOfLines, lineHeight }: IReadMoreProps,
    ): IReadMoreState => {
        const isOverflown = textEl.scrollHeight > textEl.clientHeight;

        // `fontSize` is needed because maxHeight is set in `em`;  This is calculated to a `px` value.
        const fontSize = parseFloat(window.getComputedStyle(textEl).fontSize) || 1;
        const maxHeight = numberOfLines * lineHeight * fontSize;
        const clientHeight = textEl.clientHeight;
        const shouldShowLessButton = showingMore && clientHeight > maxHeight;

        return { isOverflown, shouldShowLessButton };
    };

    handleTextResizeChange = throttle(([textEl]: ResizeObserverEntry[], props: IReadMoreProps) => {
        if (this.skipNextResizeCallback) {
            this.skipNextResizeCallback = false;
            return;
        }

        const next = this.computeOverflowState(textEl.target, props);

        this.setState((prevState) => {
            const isOverflownChanged = prevState.isOverflown !== next.isOverflown;
            const shouldShowLessButtonChanged =
                prevState.shouldShowLessButton !== next.shouldShowLessButton;

            const result = {} as IReadMoreState;
            if (isOverflownChanged) {
                result.isOverflown = next.isOverflown;
            }
            if (shouldShowLessButtonChanged) {
                result.shouldShowLessButton = next.shouldShowLessButton;
            }
            if (!isOverflownChanged && !shouldShowLessButtonChanged) {
                return null;
            }

            return result;
        });
    }, 200);

    // This is currently supported in all of our supported browsers.
    textResizeObserver: ResizeObserver | undefined =
        window.ResizeObserver &&
        new ResizeObserver((entries) => {
            this.handleTextResizeChange(entries, this.props);
        });

    componentDidMount() {
        if (this.textRef.current) {
            // If no browser support fallback to this.
            if (!this.textResizeObserver) {
                this.setState({
                    isOverflown:
                        this.textRef.current.scrollHeight > this.textRef.current.clientHeight,
                });
            } else {
                // Compute overflow synchronously during mount so React can batch the
                // resulting setState with all other lifecycle updates before first paint.
                // Skip the initial ResizeObserver callback that observe() will fire to
                // avoid an unbatched re-render storm when many instances mount together.
                this.skipNextResizeCallback = true;
                this.setState(this.computeOverflowState(this.textRef.current, this.props));
                this.textResizeObserver.observe(this.textRef.current);
            }
        }
    }

    componentWillUnmount() {
        if (this.textResizeObserver && this.textRef.current) {
            this.textResizeObserver.disconnect();
        }
    }

    private handleShowMoreClick = (e: React.MouseEvent) => {
        if (this.props.showingMore && this.props.onScrollBackToButton) {
            this.props.onScrollBackToButton();
        }

        this.props.onShowingMoreToggle(!this.props.showingMore);
        e.stopPropagation();
    };

    render() {
        const { isOverflown, shouldShowLessButton } = this.state;
        const {
            showingMore,
            children,
            numberOfLines,
            lineHeight,
            moreText,
            lessText,
            "data-testid": dataTestId,
            size,
            entityId,
        } = this.props;
        return (
            <div className="readMoreContainer" data-testid={dataTestId}>
                <div
                    className={cn("readMore", {
                        "readMore-truncated": !showingMore && isOverflown,
                        "readMore-visible": showingMore,
                    })}
                    ref={this.textRef}
                    style={
                        {
                            "--rm-lines": numberOfLines,
                            "--rm-line-height": lineHeight,
                        } as ReadMoreStyle
                    }
                >
                    <div className="zreadMore-trimmedText">{children}</div>
                </div>
                {(isOverflown || shouldShowLessButton) && (
                    <div className="readMore-link">
                        <BTButtonDeprecated
                            data-testid={showingMore ? "showLess" : "showMore"}
                            type="link"
                            size={size}
                            isolated={true}
                            onClick={this.handleShowMoreClick}
                            data-read-more-button
                            id={
                                entityId && showingMore
                                    ? `showLess_${entityId}`
                                    : `showMore_${entityId}`
                            }
                        >
                            {showingMore ? lessText : moreText}
                        </BTButtonDeprecated>
                    </div>
                )}
            </div>
        );
    }
}
